import React from 'react';
import { useAuth } from '../context/AuthContext';
import { Navigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Bot, Sparkles, Shield, Zap } from 'lucide-react';

const Login = () => {
  const { login, isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="animate-pulse text-slate-400">Loading...</div>
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/dashboard/agents" replace />;
  }

  return (
    <div className="min-h-screen bg-slate-950 flex flex-col">
      {/* Header */}
      <header className="border-b border-slate-800/50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-lg flex items-center justify-center">
              <Bot className="h-5 w-5 text-white" />
            </div>
            <span className="text-xl font-bold text-white">BuildAIHQ</span>
          </div>
        </div>
      </header>

      {/* Hero */}
      <main className="flex-1 flex items-center justify-center px-6">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-indigo-500/10 border border-indigo-500/20 text-indigo-400 text-sm mb-8">
            <Sparkles className="h-4 w-4" />
            AI-Powered Chat Agents
          </div>
          
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6 leading-tight">
            Build Intelligent
            <span className="bg-gradient-to-r from-indigo-400 to-purple-400 bg-clip-text text-transparent"> AI Agents </span>
            for Your Website
          </h1>
          
          <p className="text-xl text-slate-400 mb-12 max-w-2xl mx-auto">
            Create custom chatbots trained on your data. Add knowledge from FAQs, documents, 
            and websites. Deploy anywhere with a simple embed code.
          </p>

          <Button 
            onClick={login}
            size="lg"
            className="bg-indigo-600 hover:bg-indigo-500 text-white px-8 py-6 text-lg rounded-xl shadow-lg shadow-indigo-500/25 transition-all hover:shadow-indigo-500/40"
          >
            Get Started with Google
          </Button>

          {/* Features */}
          <div className="grid md:grid-cols-3 gap-6 mt-20">
            <div className="p-6 rounded-2xl bg-slate-900/50 border border-slate-800/50">
              <div className="w-12 h-12 rounded-xl bg-indigo-500/10 flex items-center justify-center mb-4 mx-auto">
                <Zap className="h-6 w-6 text-indigo-400" />
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">RAG-Powered</h3>
              <p className="text-slate-400 text-sm">
                Train your agent on custom knowledge bases for accurate, contextual responses.
              </p>
            </div>
            
            <div className="p-6 rounded-2xl bg-slate-900/50 border border-slate-800/50">
              <div className="w-12 h-12 rounded-xl bg-purple-500/10 flex items-center justify-center mb-4 mx-auto">
                <Shield className="h-6 w-6 text-purple-400" />
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">Domain Control</h3>
              <p className="text-slate-400 text-sm">
                Restrict your widget to specific domains with built-in allowlist security.
              </p>
            </div>
            
            <div className="p-6 rounded-2xl bg-slate-900/50 border border-slate-800/50">
              <div className="w-12 h-12 rounded-xl bg-emerald-500/10 flex items-center justify-center mb-4 mx-auto">
                <Bot className="h-6 w-6 text-emerald-400" />
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">Easy Embed</h3>
              <p className="text-slate-400 text-sm">
                Deploy your agent anywhere with a simple script tag. No coding required.
              </p>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-slate-800/50 py-6">
        <div className="max-w-7xl mx-auto px-6 text-center text-slate-500 text-sm">
          © 2025 BuildAIHQ. All rights reserved.
        </div>
      </footer>
    </div>
  );
};

export default Login;
